import { Flex, Spinner, Stack, Text } from '@chakra-ui/react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa'

import { PageInfo } from '@/types'

import { Button } from './button'
import { NativeSelectField, NativeSelectRoot } from './native-select'

interface PagerProps {
  pageInfo: PageInfo
  loading?: boolean
  setBeforeCursor: (cursor: string | undefined) => void
  setAfterCursor: (cursor: string | undefined) => void
  pageSizeOptions?: number[]
  currentPageSize?: number
  onPageSizeChange?: (pageSize: number) => void
  showPageSizeSelector?: boolean
  /** When true, buttons show only icons (no "Previous"/"Next" text) to save space */
  compact?: boolean
}

interface PaginationInfo {
  startCursor?: string
  endCursor?: string
  hasNextPage: boolean
  hasPreviousPage: boolean
}

interface NewPaginationProps {
  pageInfo?: PaginationInfo
  totalCount: number
  pageSize: number
  onNextPage: (pageInfo: PaginationInfo) => void
  onPreviousPage: (pageInfo: PaginationInfo) => void
  pageSizeOptions?: number[]
  onPageSizeChange?: (pageSize: number) => void
  showPageSizeSelector?: boolean
  /** When true, buttons show only icons (no "Previous"/"Next" text) to save space */
  compact?: boolean
}

type PaginationProps = PagerProps | NewPaginationProps

function isPagerProps(props: PaginationProps): props is PagerProps {
  return 'setBeforeCursor' in props && 'setAfterCursor' in props
}

export const Pagination = (props: PaginationProps) => {
  const { t } = useTranslation()

  const pageSizeOptions = props.pageSizeOptions || [10, 20, 50]
  const showPageSizeSelector = props.showPageSizeSelector !== false
  const { onPageSizeChange } = props
  const compact = props.compact ?? false

  const PageSizeSelector = () => {
    if (!showPageSizeSelector || !onPageSizeChange) return null

    const currentPageSize = isPagerProps(props)
      ? props.currentPageSize || pageSizeOptions[0]
      : props.pageSize

    return (
      <Flex alignItems="center" gap={2} flexShrink={0}>
        {!compact && (
          <Text fontSize="sm" color="gray.600" whiteSpace="nowrap">
            {t('global:itemsPerPage')}:
          </Text>
        )}
        <NativeSelectRoot size="sm" width="20" variant="outline">
          <NativeSelectField
            value={currentPageSize}
            onChange={(e) =>
              onPageSizeChange(Number.parseInt(e.target.value, 10))
            }
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </NativeSelectField>
        </NativeSelectRoot>
      </Flex>
    )
  }

  const buttonSize = compact ? 'xs' : undefined
  const buttonRounded = compact ? 'md' : undefined
  const buttonGap = compact ? 1 : 3

  const NavigationButtons = ({ loading = false }: { loading?: boolean }) => {
    if (isPagerProps(props)) {
      const { pageInfo, setBeforeCursor, setAfterCursor } = props

      return (
        <Flex gap={buttonGap}>
          <Button
            size={buttonSize}
            rounded={buttonRounded}
            disabled={loading || !pageInfo.hasPreviousPage}
            onClick={async () => {
              setBeforeCursor(pageInfo?.startCursor)
              setAfterCursor(undefined)
            }}
            title={compact ? t('global:previous') : undefined}
          >
            {loading ? <Spinner /> : <FaChevronLeft />}
            {!compact && t('global:previous')}
          </Button>
          <Button
            size={buttonSize}
            rounded={buttonRounded}
            disabled={loading || !pageInfo.hasNextPage}
            onClick={async () => {
              setAfterCursor(pageInfo?.endCursor)
              setBeforeCursor(undefined)
            }}
            title={compact ? t('global:next') : undefined}
          >
            {loading ? <Spinner /> : <FaChevronRight />}
            {!compact && t('global:next')}
          </Button>
        </Flex>
      )
    }

    const { pageInfo, onNextPage, onPreviousPage } = props as NewPaginationProps

    return (
      <Flex gap={buttonGap}>
        <Button
          size={buttonSize}
          rounded={buttonRounded}
          disabled={!pageInfo?.hasPreviousPage}
          onClick={() => pageInfo && onPreviousPage(pageInfo)}
          title={compact ? t('global:previous') : undefined}
        >
          <FaChevronLeft />
          {!compact && t('global:previous')}
        </Button>

        <Button
          size={buttonSize}
          rounded={buttonRounded}
          disabled={!pageInfo?.hasNextPage}
          onClick={() => pageInfo && onNextPage(pageInfo)}
          title={compact ? t('global:next') : undefined}
        >
          <FaChevronRight />
          {!compact && t('global:next')}
        </Button>
      </Flex>
    )
  }

  if (isPagerProps(props)) {
    const { loading = false, pageInfo } = props
    const hasNavigation = pageInfo?.hasPreviousPage || pageInfo?.hasNextPage

    if (compact) {
      return (
        <Flex justifyContent="flex-end" mt={2} gap={2}>
          <PageSizeSelector />
          {hasNavigation && <NavigationButtons loading={loading} />}
        </Flex>
      )
    }

    return (
      <Stack gap={4} my={5}>
        <Flex
          display={{ base: 'flex', md: 'none' }}
          direction="column"
          alignItems="center"
          gap={4}
        >
          {hasNavigation && <NavigationButtons loading={loading} />}
          <PageSizeSelector />
        </Flex>

        <Flex
          display={{ base: 'none', md: 'flex' }}
          alignItems="center"
          justifyContent={hasNavigation ? 'center' : 'flex-end'}
          position="relative"
          width="100%"
        >
          {hasNavigation && <NavigationButtons loading={loading} />}

          {showPageSizeSelector && onPageSizeChange && (
            <Flex position={hasNavigation ? 'absolute' : 'relative'} right={0}>
              <PageSizeSelector />
            </Flex>
          )}
        </Flex>
      </Stack>
    )
  }

  if (compact) {
    return (
      <Flex justifyContent="flex-end" mt={2} gap={2}>
        <PageSizeSelector />
        <NavigationButtons />
      </Flex>
    )
  }

  return (
    <Stack gap={4} mt={10}>
      <Flex
        display={{ base: 'flex', md: 'none' }}
        direction="column"
        alignItems="center"
        gap={4}
      >
        <NavigationButtons />
        <PageSizeSelector />
      </Flex>

      <Flex
        display={{ base: 'none', md: 'flex' }}
        alignItems="center"
        justifyContent="center"
        position="relative"
        width="100%"
      >
        <NavigationButtons />

        <Flex position="absolute" right={0}>
          <PageSizeSelector />
        </Flex>
      </Flex>
    </Stack>
  )
}
